import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { ScreenHeader, Section } from '../../components/common';
import { legalUrl } from '../../lib/publicSite';

/** Strip SDK prefixes so a failure reads as a sentence, not a stack trace. */
const reasonOf = (e: unknown) => String(e).replace(/^(FirebaseError|Error):\s*/, '').slice(0, 220);

export function PrivacyScreen() {
  const { t } = useI18n();
  const app = useApp();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<'export' | 'delete' | null>(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const exportData = async () => {
    setBusy('export');
    setError('');
    setNote('');
    try {
      const data = await app.backend.exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'astrally-export.json';
      a.click();
      URL.revokeObjectURL(url);
      setNote('Exported as astrally-export.json — check your downloads.');
    } catch (e) {
      setError(`Export failed, so nothing was downloaded. ${reasonOf(e)}`);
    } finally {
      setBusy(null);
    }
  };

  /**
   * The most destructive action in the app, so it must never be ambiguous.
   * A failure here used to look identical to success — silent either way —
   * which could leave someone believing their data was gone when it was not.
   */
  const deleteAccount = async () => {
    if (!confirm(t('profile.deleteAccount.confirm'))) return;
    setBusy('delete');
    setError('');
    setNote('');
    try {
      await app.resetAll();
      navigate('/onboarding');
      // No setBusy(null) on success: this screen unmounts, and re-enabling the
      // button first would briefly offer a second press of an irreversible action.
    } catch (e) {
      setError(`Your account was NOT deleted — nothing has been removed. ${reasonOf(e)}`);
      setBusy(null);
    }
  };

  return (
    <div className="screen">
      <ScreenHeader title={t('profile.privacy')} back />

      <Section title="Where your data lives">
        <div className="card col small" style={{ lineHeight: 1.6 }}>
          <div>
            <b>On this device only:</b> your exact birth date, time and place, your full calculated
            charts, and your Ally conversation history.
          </div>
          <div>
            <b>Synced for matching (with your consent):</b> derived chart features (sign-level
            summaries), broad age band, city/region, connection intentions, profile scores, and
            memories you explicitly approved for cloud use.
          </div>
          <div>
            <b>Never shown to recommended users:</b> your exact birth details, private memories,
            internal safety signals, and raw scoring formulas.
          </div>
          <div className="banner banner--info">{t('profile.privacy.birth')}</div>
        </div>
      </Section>

      <Section title="Controls">
        <div className="col">
          <button className="btn btn--ghost btn--block" disabled={busy !== null} onClick={exportData}>
            {busy === 'export' ? <span className="spinner" /> : t('profile.export')}
          </button>
          <button className="btn btn--danger btn--block" disabled={busy !== null} onClick={deleteAccount}>
            {busy === 'delete' ? <span className="spinner" /> : t('profile.deleteAccount')}
          </button>
          {error && <div className="banner banner--danger small" role="alert">{error}</div>}
          {note && (
            <div
              className="banner small"
              role="status"
              style={{ background: 'var(--ok-soft)', color: 'var(--ok)' }}
            >
              {note}
            </div>
          )}
        </div>
      </Section>

      <div className="tiny faint" style={{ marginTop: 16, lineHeight: 1.6 }}>
        The summary above is the current statement of how Astrally handles your data, and account
        deletion is the button above it. The full published documents are here:{' '}
        <a href={legalUrl('privacy')} target="_blank" rel="noreferrer" className="accent">
          Privacy Policy
        </a>{' '}
        ·{' '}
        <a href={legalUrl('terms')} target="_blank" rel="noreferrer" className="accent">
          Terms of Use
        </a>
      </div>
    </div>
  );
}
